import { addAddressFields } from 'app-components/flux/addresses/addressFields';
import type { Address } from 'app-components/flux/addresses/addressTypes';
import { getDefaultBusiness, getOpenBusinesses } from 'app-components/flux/business/businessSelectors';
import { BUSINESS_REDUCER_KEY } from 'app-components/flux/business/businessTypes';
import { type Customer, type CustomerProps, mkCustomer } from 'app-components/flux/customers/customersTypes';
import { ValidationTrigger, createManagerFactory, formBuilder } from 'app-components/utils/createForm/formBuilder';
import { validateEmail } from 'app-components/utils/createForm/utils';
import RootState from 'app-components/utils/redux-store/rootState';

const nameError = 'Either a full name or a customer name is required';

type ReduxState = Pick<RootState, typeof BUSINESS_REDUCER_KEY>;

const isEmptyAddress = (address: Address) =>
  !(address.zipCode || address.line1 || address.line2 || address.city || address.state);

const hideBusinessIdField = (store: ReduxState) => getOpenBusinesses(store).size <= 1;

const form = addAddressFields(
  formBuilder({
    init: ({ input, store }: { input: Customer | CustomerProps; store: ReduxState }) => {
      const customer = 'toObject' in input ? input.toObject() : input;
      const defaultBusinessId = getDefaultBusiness(store)?.id ?? '';
      const businessId =
        customer.businessId || (!customer.id || hideBusinessIdField(store) ? defaultBusinessId : undefined);
      return {
        customer: {
          ...customer,
          businessId,
        },
        addressEnabled: !!customer?.address && !isEmptyAddress(customer.address),
        address: customer.address ?? {},
      };
    },
  }),
)
  .addField('businessName', {
    selector: (state) => state.customer.businessName,
    onValueChange(draft, value) {
      draft.customer.businessName = value;
    },
    validation: {
      oneNameRequired: {
        triggers: ValidationTrigger.onSubmit,
        isValid: ({ customer: { businessName, firstName, lastName } }: { customer: CustomerProps }) =>
          !!businessName || !!(firstName && lastName),
        error: nameError,
      },
    },
  })
  .addField('firstName', {
    selector: (state) => state.customer.firstName,
    onValueChange(draft, value) {
      draft.customer.firstName = value;
    },
    validation: {
      oneNameRequired: {
        triggers: ValidationTrigger.onSubmit,
        isValid: ({ customer: { businessName, firstName } }: { customer: CustomerProps }) =>
          !!(businessName || firstName),
        error: nameError,
      },
    },
  })
  .addField('lastName', {
    selector: (state) => state.customer.lastName,
    onValueChange(draft, value) {
      draft.customer.lastName = value;
    },
    validation: {
      oneNameRequired: {
        triggers: ValidationTrigger.onSubmit,
        isValid: ({ customer: { businessName, lastName } }: { customer: CustomerProps }) =>
          !!(businessName || lastName),
        error: nameError,
      },
    },
  })
  .addField('businessId', {
    selector: (state) => state.customer.businessId,
    onValueChange(draft, value) {
      draft.customer.businessId = value;
    },
    validation: {
      required: {
        triggers: ValidationTrigger.onSubmit,
        // Always valid if hidden, but we will try and autofill with default.
        isValid: (state, store) => hideBusinessIdField(store) || !!state.customer.businessId,
        error: 'Required',
      },
    },
    helperTextSelector() {
      return 'Business primarily associated with this client';
    },
    hiddenSelector(_state, store) {
      return hideBusinessIdField(store);
    },
  })
  .addField('email', {
    selector: (state) => state.customer.email,
    onValueChange(draft, value) {
      draft.customer.email = value;
    },
    validation: {
      invalidFormat: {
        triggers: ValidationTrigger.onSubmit,
        isValid: (state) => !state.customer.email || validateEmail(state.customer.email),
        error: 'Invalid email',
      },
    },
  })
  .addField('phone', {
    selector: (state) => state.customer.phoneNumber,
    onValueChange(draft, value) {
      draft.customer.phoneNumber = value;
    },
  })
  .addField('note', {
    selector: (state) => state.customer.privateNotes,
    onValueChange(draft, value) {
      draft.customer.privateNotes = value;
    },
  })
  .build({
    submitTransform: (state) => {
      const record = mkCustomer(state.customer);
      if (state.addressEnabled) {
        return record.set('address', state.address);
      }
      return record.set('address', { line1: '', line2: '', city: '', state: '', zipCode: '' });
    },
  });

export const customerFormFactory = createManagerFactory(form);
